import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  fr: {
    // Navigation & Header
    'nav.catalog': 'Catalogue',
    'nav.admin': 'Administration',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.profile': 'Mon profil',
    'nav.downloads': 'Téléchargements',
    'header.subtitle': 'Catalogue Tarif 2026',
    'header.role_admin': 'Administrateur',
    'header.role_reseller': 'Revendeur',
    'header.contact_us': 'Contactez-nous',
    'catalog.search_short': 'Rechercher...',
    
    // Catalog
    'catalog.search': 'Rechercher un produit...',
    'catalog.filters': 'Filtres',
    'catalog.categories': 'Catégories',
    'catalog.all_products': 'Tous les produits',
    'catalog.no_results': 'Aucun produit trouvé',
    'catalog.no_family_results': 'Aucune famille trouvée',
    'catalog.try_other_criteria': 'Essayez de modifier vos critères de recherche ou sélectionnez une autre catégorie.',
    'catalog.compare': 'Comparer',
    'catalog.add_to_compare': 'Ajouter au comparateur',
    'catalog.remove_from_compare': 'Retirer du comparateur',
    'catalog.in_comparator': 'Dans le comparateur',
    'catalog.view_categories': 'Vue par catégories',
    'catalog.view_detailed': 'Vue détaillée',
    
    // Product card
    'product.hidden': 'Masqué',
    'product.discontinued': 'Plus disponible',
    'product.special_offer': 'Offre spéciale',
    'product.range': 'Portée',
    'product.pan': 'Plateau',
    'product.public_price': 'MSRP',
    'product.public_price_tooltip': 'Prix public officiel',
    'product.promo_reseller_price': 'Prix promo revendeur',
    'product.price_company': 'Prix',
    'product.on_request': 'Sur demande',
    'product.login_for_price': 'Connectez-vous pour voir le prix',
    'product.login_for_reseller_price': 'Connectez-vous pour voir le prix revendeur',
    'product.savings': 'Économie',
    'product.photos': 'photos',
    'product.edit': 'Éditer le produit',
    
    // Product details
    'product.reference': 'Référence',
    'product.reference_and': 'Référence A&D',
    'product.capacity': 'Capacité',
    'product.resolution': 'Résolution',
    'product.pan_size': 'Taille du plateau',
    'product.specifications': 'Spécifications',
    'product.features': 'Caractéristiques',
    'product.description': 'Description',
    'product.price': 'Prix',
    'product.reseller_price': 'Prix revendeur',
    'product.brochure': 'Brochure',
    'product.manual': 'Manuel',
    'product.website': 'Site A&D',
    'product.preview': 'Prévisualisation',
    'product.compatible_products': 'Produits compatibles',
    'product.available_options': 'Options disponibles',
    'product.loading': 'Chargement...',
    'product.check_compatibility': 'Vérifier compatibilité',
    'product.range_compatible': 'Gamme',
    'product.compatible': 'compatible',
    'product.models_compatible': 'modèles compatibles',
    'product.models': 'modèles',
    'product.hide': 'Masquer',
    'product.show_models': 'Voir les modèles',
    'product.is_compatible': 'est compatible',
    'product.no_compatible_model': 'Aucun modèle',
    'product.option_for': 'Option pour',
    'product.reference_label': 'Référence',
    'product.site': 'Site',
    
    // Actions
    'action.save': 'Enregistrer',
    'action.cancel': 'Annuler',
    'action.edit': 'Éditer',
    'action.delete': 'Supprimer',
    'action.add': 'Ajouter',
    'action.upload': 'Uploader un fichier',
    'action.share': 'Partager',
    'action.link_copied': 'Lien copié',
    'action.link_copied_desc': 'Le lien du produit a été copié dans le presse-papiers.',
    
    // Favorites
    'favorites.add': 'Ajouter aux favoris',
    'favorites.remove': 'Retirer des favoris',
    
    // Quote
    'quote.add': 'Ajouter au devis',
    'quote.remove': 'Retirer du devis',
    'quote.added': 'Ajouté au devis',
    'quote.request': 'Demander un devis',
    'quote.title': 'Demande de devis',
    'quote.items_count': 'produit(s) dans votre demande',
    'quote.clear': 'Vider le panier',
    'quote.selected_products': 'Produits sélectionnés',
    'quote.product': 'Produit',
    'quote.public': 'Public',
    'quote.unit_price': 'Prix unit.',
    'quote.quantity': 'Quantité',
    'quote.subtotal': 'Sous-total',
    'quote.your_price': 'Votre prix :',
    'quote.indicative_total': 'Total indicatif',
    'quote.saving': 'Économie',
    'quote.final_price_note': 'Prix final confirmé par devis',
    'quote.your_details': 'Vos coordonnées',
    'quote.contact_name': 'Nom du contact',
    'quote.your_name': 'Votre nom',
    'quote.email': 'Email',
    'quote.phone': 'Téléphone',
    'quote.message': 'Message / Notes',
    'quote.message_placeholder': 'Informations complémentaires, délais souhaités...',
    'quote.sending': 'Envoi en cours...',
    'quote.send': 'Envoyer la demande de devis',
    'quote.button': 'Devis',
    
    // Compare
    'compare.title': 'Comparaison de produits',
    'compare.description': 'Comparez jusqu\'à 4 produits côte à côte',
    'compare.clear': 'Tout effacer',
    'compare.feature': 'Caractéristique',
    'compare.price': 'Prix',
    'compare.capacity': 'Capacité',
    'compare.resolution': 'd (Résolution)',
    'compare.verification': 'e (Échelon)',
    'compare.pan_size': 'Taille plateau',
    'compare.badges': 'Caractéristiques',
    'compare.category': 'Catégorie',
    'compare.login': 'Connectez-vous',
    
    // Admin
    'admin.products': 'Produits',
    'admin.badges': 'Badges',
    'admin.images': 'Images',
    'admin.links': 'Liens',
    'admin.documents': 'Documents',
    'admin.add': 'Ajouter',
    'admin.reorder': 'Trier',
    'admin.reorder_done': 'OK',
    'admin.reorder_tooltip': 'Réordonner les catégories',
    'admin.reorder_done_tooltip': 'Terminer le réordonnancement',

    // Sidebar
    'sidebar.promotions': 'Promotions',
    'sidebar.novelties': 'Derniers arrivés',
    'sidebar.legal_metrology': 'Métrologie Légale',
    'sidebar.ex_demo': 'Ex-démo',
    'sidebar.accessories': 'Accessoires',
    'sidebar.consumables': 'Consommables',
    'sidebar.indicators': 'Indicateurs',

    // Family card
    'family.hidden': 'Masquée',
    'family.models': 'modèle',
    'family.models_plural': 'modèles',
    'family.range': 'Portée',
    'family.view_all_models': 'Voir tous les modèles',
    'family.from': 'De',
    'family.to': 'à',

    // Catalog page
    'catalog.categories_count': 'catégorie',
    'catalog.categories_count_plural': 'catégories',
    'catalog.variants_count': 'variante',
    'catalog.variants_count_plural': 'variantes',
    'catalog.products_count': 'produit',
    'catalog.products_count_plural': 'produits',
    'catalog.back_to_categories': 'Retour aux catégories',
    'catalog.back_to': 'Retour à',
    'catalog.select_category': 'Sélectionnez une catégorie pour filtrer les produits',

    // Variant groups
    'variant.models': 'modèle',
    'variant.models_plural': 'modèles',
    'variant.view_models': 'Voir les modèles',
    'variant.from': 'De',
    'variant.to': 'à',

    // Auth page
    'auth.back_to_catalog': 'Retour au catalogue',
    'auth.catalog_title': 'Catalogue A&D',
    'auth.reseller_access': 'Accès revendeurs',
    'auth.login': 'Connexion',
    'auth.signup': 'Inscription',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.confirm_password': 'Confirmer le mot de passe',
    'auth.professional_email': 'Email professionnel',
    'auth.company_name': "Nom de l'entreprise",
    'auth.sign_in': 'Se connecter',
    'auth.create_account': 'Créer mon compte',
    'auth.or': 'ou',
    'auth.continue_google': 'Continuer avec Google',
    'auth.continue_apple': 'Continuer avec Apple',
    'auth.email_placeholder': 'votre@email.com',
    'auth.company_placeholder': 'Votre entreprise',
    'auth.signup_email_placeholder': 'votre@entreprise.com',
    'auth.terms': "En vous inscrivant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.",
    'auth.error_oauth': 'Erreur de connexion',
    'auth.welcome': 'Bienvenue !',
    'auth.connected': 'Vous êtes maintenant connecté.',
    'auth.error': 'Erreur',
    'auth.error_generic': 'Une erreur est survenue lors de la connexion.',
    'auth.error_login': 'Erreur de connexion',
    'auth.error_invalid_credentials': 'Email ou mot de passe incorrect',
    'auth.error_passwords_mismatch': 'Les mots de passe ne correspondent pas.',
    'auth.error_password_length': 'Le mot de passe doit contenir au moins 6 caractères.',
    'auth.error_already_registered': 'Cet email est déjà enregistré. Essayez de vous connecter.',
    'auth.error_signup': "Erreur d'inscription",
    'auth.signup_success': 'Inscription réussie !',
    'auth.signup_check_email': 'Veuillez vérifier votre email pour confirmer votre compte.',

    // AI Chat Widget
    'chat.title': 'Assistant technique A&D',
    'chat.ask_question': 'Posez votre question technique',
    'chat.example': 'Ex: "Quel est le poids de la GX-124A ?" ou "Quelle est la résolution du FX-300i ?"',
    'chat.placeholder': 'Posez votre question...',
    'chat.sources': 'Sources :',
    'chat.brochure': 'Brochure',
    'chat.manual': 'Manuel',
    'chat.aria_open': "Ouvrir l'assistant IA",
    'chat.not_authenticated': 'Non authentifié',

    // Profile
    'profile.my_info': 'Mes coordonnées',
    'profile.name': 'Nom complet',
    'profile.phone': 'Téléphone',
    'profile.company': 'Société',
    'profile.official_documents': 'Documents officiels A&D',
    'profile.no_documents': 'Aucun document disponible pour le moment.',

    // Common
    'common.save': 'Enregistrer',
    'common.download': 'Télécharger',

    // Search assistant
    'assistant.open': 'Assistant de recherche',
    'assistant.title': 'Assistant de recherche',
    'assistant.subtitle': 'Décrivez votre besoin ou laissez-vous guider pas à pas pour trouver la balance idéale.',
    'assistant.tab_free': 'Décrire le besoin',
    'assistant.tab_wizard': 'Pas à pas',
    'assistant.free_placeholder': 'Collez ici un email client ou décrivez votre besoin (ex : "Je cherche une balance pour peser environ 600 g avec une précision de 10 mg, plateau standard")',
    'assistant.analyze': 'Analyser',
    'assistant.understood': 'Compris',
    'assistant.no_notes': 'Critères extraits.',
    'assistant.badge_capacity': 'Capacité',
    'assistant.badge_resolution': 'Résolution',
    'assistant.badge_pan': 'Plateau',
    'assistant.badge_legal_metrology': 'Métrologie légale',
    'assistant.redo': 'Recommencer',
    'assistant.see_results': 'Voir les résultats',
    'assistant.error_title': 'Erreur',
    'assistant.error_generic': "Impossible d'analyser le texte.",
    'assistant.wizard_empty_title': 'Critères manquants',
    'assistant.wizard_empty_desc': 'Renseignez au moins une capacité, une résolution ou un plateau.',
    'assistant.wizard_step': 'Étape',
    'assistant.wizard_of': 'sur',
    'assistant.wizard_back': 'Précédent',
    'assistant.wizard_next': 'Suivant',
    'assistant.wizard_finish': 'Voir les résultats',
    'assistant.wizard_skip': 'Passer',
    'assistant.wizard_capacity_title': 'Quelle capacité approximative ?',
    'assistant.wizard_resolution_title': 'Quelle précision (résolution) ?',
    'assistant.wizard_pan_title': 'Quelle taille de plateau ? (optionnel)',
    'assistant.wizard_usage_title': 'Type d\'usage',
    // Wizard steps (SearchWizardSteps)
    'assistant.step1_title': 'Quelle capacité maximale ?',
    'assistant.step1_desc': 'La charge la plus lourde que vous aurez à peser.',
    'assistant.step2_title': 'Quelle précision (résolution) ?',
    'assistant.step2_desc': 'Le plus petit incrément lisible sur l\'afficheur.',
    'assistant.step3_title': 'Taille de plateau souhaitée',
    'assistant.step3_desc': 'Optionnel — laissez « Indifférent » si peu importe.',
    'assistant.cap_micro': 'Jusqu\'à 100 g (micro)',
    'assistant.cap_small': 'Jusqu\'à 500 g (petit)',
    'assistant.cap_mid': 'Jusqu\'à 2 kg (moyen)',
    'assistant.cap_large': 'Jusqu\'à 6 kg (grand)',
    'assistant.cap_xlarge': '30 kg et plus',
    'assistant.pan_small': 'Petit (≤ 100 mm)',
    'assistant.pan_mid': 'Moyen (100–180 mm)',
    'assistant.pan_large': 'Grand (≥ 180 mm)',
    'assistant.pan_any': 'Indifférent',
    'assistant.cancel': 'Annuler',
    'assistant.back': 'Précédent',
    'assistant.next': 'Suivant',
    'assistant.recap': 'Récapitulatif :',
    'assistant.range_min': 'Min',
    'assistant.range_max': 'Max',
    'assistant.res_finest': 'Plus fine',
    'assistant.res_coarsest': 'Plus large',
    'assistant.loading_specs': 'Chargement des valeurs du catalogue…',

    // Match quality badges
    'match.exact': 'Correspond exactement',
    'match.finer': 'Plus précis',
    'match.larger': 'Capacité supérieure',
    'match.pan_only': 'Plateau correspondant',
    'match.capacity_label': 'Capacité',
    'match.resolution_label': 'Résolution',
    'match.pan_label': 'Plateau',
  },
  en: {
    // Navigation & Header
    'nav.catalog': 'Catalog',
    'nav.admin': 'Administration',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    'nav.profile': 'My profile',
    'nav.downloads': 'Downloads',
    'header.subtitle': 'Price Catalog 2026',
    'header.role_admin': 'Administrator',
    'header.role_reseller': 'Reseller',
    'header.contact_us': 'Contact us',
    'catalog.search_short': 'Search...',
    
    // Catalog
    'catalog.search': 'Search for a product...',
    'catalog.filters': 'Filters',
    'catalog.categories': 'Categories',
    'catalog.all_products': 'All products',
    'catalog.no_results': 'No products found',
    'catalog.no_family_results': 'No families found',
    'catalog.try_other_criteria': 'Try changing your search criteria or select another category.',
    'catalog.compare': 'Compare',
    'catalog.add_to_compare': 'Add to compare',
    'catalog.remove_from_compare': 'Remove from compare',
    'catalog.in_comparator': 'In comparator',
    'catalog.view_categories': 'Category view',
    'catalog.view_detailed': 'Detailed view',
    
    // Product card
    'product.hidden': 'Hidden',
    'product.discontinued': 'Discontinued',
    'product.special_offer': 'Special offer',
    'product.range': 'Range',
    'product.pan': 'Pan',
    'product.public_price': 'MSRP',
    'product.public_price_tooltip': 'Manufacturer\'s Suggested Retail Price',
    'product.promo_reseller_price': 'Promo reseller price',
    'product.price_company': 'Price',
    'product.on_request': 'On request',
    'product.login_for_price': 'Login to see price',
    'product.login_for_reseller_price': 'Login to see reseller price',
    'product.savings': 'Savings',
    'product.photos': 'photos',
    'product.edit': 'Edit product',
    
    // Product details
    'product.reference': 'Reference',
    'product.reference_and': 'A&D Reference',
    'product.capacity': 'Capacity',
    'product.resolution': 'Resolution',
    'product.pan_size': 'Pan size',
    'product.specifications': 'Specifications',
    'product.features': 'Features',
    'product.description': 'Description',
    'product.price': 'Price',
    'product.reseller_price': 'Reseller price',
    'product.brochure': 'Brochure',
    'product.manual': 'Manual',
    'product.website': 'A&D Website',
    'product.preview': 'Preview',
    'product.compatible_products': 'Compatible products',
    'product.available_options': 'Available options',
    'product.loading': 'Loading...',
    'product.check_compatibility': 'Check compatibility',
    'product.range_compatible': 'Range',
    'product.compatible': 'compatible',
    'product.models_compatible': 'compatible models',
    'product.models': 'models',
    'product.hide': 'Hide',
    'product.show_models': 'Show models',
    'product.is_compatible': 'is compatible',
    'product.no_compatible_model': 'No model',
    'product.option_for': 'Option for',
    'product.reference_label': 'Reference',
    'product.site': 'Site',
    
    // Actions
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.edit': 'Edit',
    'action.delete': 'Delete',
    'action.add': 'Add',
    'action.upload': 'Upload a file',
    'action.share': 'Share',
    'action.link_copied': 'Link copied',
    'action.link_copied_desc': 'The product link has been copied to your clipboard.',
    
    // Favorites
    'favorites.add': 'Add to favorites',
    'favorites.remove': 'Remove from favorites',
    
    // Quote
    'quote.add': 'Add to quote',
    'quote.remove': 'Remove from quote',
    'quote.added': 'Added to quote',
    'quote.request': 'Request a quote',
    'quote.title': 'Quote request',
    'quote.items_count': 'product(s) in your request',
    'quote.clear': 'Clear cart',
    'quote.selected_products': 'Selected products',
    'quote.product': 'Product',
    'quote.public': 'Public',
    'quote.unit_price': 'Unit price',
    'quote.quantity': 'Quantity',
    'quote.subtotal': 'Subtotal',
    'quote.your_price': 'Your price:',
    'quote.indicative_total': 'Indicative total',
    'quote.saving': 'Saving',
    'quote.final_price_note': 'Final price confirmed by quote',
    'quote.your_details': 'Your details',
    'quote.contact_name': 'Contact name',
    'quote.your_name': 'Your name',
    'quote.email': 'Email',
    'quote.phone': 'Phone',
    'quote.message': 'Message / Notes',
    'quote.message_placeholder': 'Additional information, desired delivery times...',
    'quote.sending': 'Sending...',
    'quote.send': 'Send quote request',
    'quote.button': 'Quote',
    
    // Compare
    'compare.title': 'Product comparison',
    'compare.description': 'Compare up to 4 products side by side',
    'compare.clear': 'Clear all',
    'compare.feature': 'Feature',
    'compare.price': 'Price',
    'compare.capacity': 'Capacity',
    'compare.resolution': 'd (Resolution)',
    'compare.verification': 'e (Verification)',
    'compare.pan_size': 'Pan size',
    'compare.badges': 'Features',
    'compare.category': 'Category',
    'compare.login': 'Login',
    
    // Admin
    'admin.products': 'Products',
    'admin.badges': 'Badges',
    'admin.images': 'Images',
    'admin.links': 'Links',
    'admin.documents': 'Documents',
    'admin.add': 'Add',
    'admin.reorder': 'Sort',
    'admin.reorder_done': 'OK',
    'admin.reorder_tooltip': 'Reorder categories',
    'admin.reorder_done_tooltip': 'Finish reordering',

    // Sidebar
    'sidebar.promotions': 'Promotions',
    'sidebar.novelties': 'New arrivals',
    'sidebar.legal_metrology': 'Legal Metrology',
    'sidebar.ex_demo': 'Ex-demo',
    'sidebar.accessories': 'Accessories',
    'sidebar.consumables': 'Consumables',
    'sidebar.indicators': 'Indicators',

    // Family card
    'family.hidden': 'Hidden',
    'family.models': 'model',
    'family.models_plural': 'models',
    'family.range': 'Range',
    'family.view_all_models': 'View all models',
    'family.from': 'From',
    'family.to': 'to',

    // Catalog page
    'catalog.categories_count': 'category',
    'catalog.categories_count_plural': 'categories',
    'catalog.variants_count': 'variant',
    'catalog.variants_count_plural': 'variants',
    'catalog.products_count': 'product',
    'catalog.products_count_plural': 'products',
    'catalog.back_to_categories': 'Back to categories',
    'catalog.back_to': 'Back to',
    'catalog.select_category': 'Select a category to filter products',

    // Variant groups
    'variant.models': 'model',
    'variant.models_plural': 'models',
    'variant.view_models': 'View models',
    'variant.from': 'From',
    'variant.to': 'to',

    // Auth page
    'auth.back_to_catalog': 'Back to catalog',
    'auth.catalog_title': 'A&D Catalog',
    'auth.reseller_access': 'Reseller access',
    'auth.login': 'Login',
    'auth.signup': 'Sign up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirm_password': 'Confirm password',
    'auth.professional_email': 'Professional email',
    'auth.company_name': 'Company name',
    'auth.sign_in': 'Sign in',
    'auth.create_account': 'Create account',
    'auth.or': 'or',
    'auth.continue_google': 'Continue with Google',
    'auth.continue_apple': 'Continue with Apple',
    'auth.email_placeholder': 'your@email.com',
    'auth.company_placeholder': 'Your company',
    'auth.signup_email_placeholder': 'your@company.com',
    'auth.terms': 'By signing up, you agree to our terms of service and privacy policy.',
    'auth.error_oauth': 'Login error',
    'auth.welcome': 'Welcome!',
    'auth.connected': 'You are now connected.',
    'auth.error': 'Error',
    'auth.error_generic': 'An error occurred during login.',
    'auth.error_login': 'Login error',
    'auth.error_invalid_credentials': 'Incorrect email or password',
    'auth.error_passwords_mismatch': 'Passwords do not match.',
    'auth.error_password_length': 'Password must be at least 6 characters.',
    'auth.error_already_registered': 'This email is already registered. Try logging in.',
    'auth.error_signup': 'Sign up error',
    'auth.signup_success': 'Sign up successful!',
    'auth.signup_check_email': 'Please check your email to confirm your account.',

    // AI Chat Widget
    'chat.title': 'A&D Technical Assistant',
    'chat.ask_question': 'Ask your technical question',
    'chat.example': 'E.g.: "What is the weight of the GX-124A?" or "What is the resolution of the FX-300i?"',
    'chat.placeholder': 'Ask your question...',
    'chat.sources': 'Sources:',
    'chat.brochure': 'Brochure',
    'chat.manual': 'Manual',
    'chat.aria_open': 'Open AI assistant',
    'chat.not_authenticated': 'Not authenticated',

    // Profile
    'profile.my_info': 'My details',
    'profile.name': 'Full name',
    'profile.phone': 'Phone',
    'profile.company': 'Company',
    'profile.official_documents': 'A&D Official Documents',
    'profile.no_documents': 'No documents available at this time.',

    // Common
    'common.save': 'Save',
    'common.download': 'Download',

    // Search assistant
    'assistant.open': 'Search assistant',
    'assistant.title': 'Search assistant',
    'assistant.subtitle': 'Describe your need or follow the step-by-step guide to find the right balance.',
    'assistant.tab_free': 'Describe need',
    'assistant.tab_wizard': 'Step by step',
    'assistant.free_placeholder': 'Paste a customer email or describe your need (e.g. "I need a balance to weigh around 600 g with a 10 mg precision, standard pan")',
    'assistant.analyze': 'Analyze',
    'assistant.understood': 'Understood',
    'assistant.no_notes': 'Criteria extracted.',
    'assistant.badge_capacity': 'Capacity',
    'assistant.badge_resolution': 'Resolution',
    'assistant.badge_pan': 'Pan',
    'assistant.badge_legal_metrology': 'Legal metrology',
    'assistant.redo': 'Start over',
    'assistant.see_results': 'See results',
    'assistant.error_title': 'Error',
    'assistant.error_generic': 'Could not analyze the text.',
    'assistant.wizard_empty_title': 'Missing criteria',
    'assistant.wizard_empty_desc': 'Please provide at least a capacity, resolution or pan size.',
    'assistant.wizard_step': 'Step',
    'assistant.wizard_of': 'of',
    'assistant.wizard_back': 'Back',
    'assistant.wizard_next': 'Next',
    'assistant.wizard_finish': 'See results',
    'assistant.wizard_skip': 'Skip',
    'assistant.wizard_capacity_title': 'Approximate capacity?',
    'assistant.wizard_resolution_title': 'Required precision (resolution)?',
    'assistant.wizard_pan_title': 'Pan size? (optional)',
    'assistant.wizard_usage_title': 'Type of use',
    // Wizard steps (SearchWizardSteps)
    'assistant.step1_title': 'What maximum capacity?',
    'assistant.step1_desc': 'The heaviest load you will weigh.',
    'assistant.step2_title': 'Required precision (readability)?',
    'assistant.step2_desc': 'The smallest increment shown on the display.',
    'assistant.step3_title': 'Preferred pan size',
    'assistant.step3_desc': 'Optional — leave "Any" if it does not matter.',
    'assistant.cap_micro': 'Up to 100 g (micro)',
    'assistant.cap_small': 'Up to 500 g (small)',
    'assistant.cap_mid': 'Up to 2 kg (medium)',
    'assistant.cap_large': 'Up to 6 kg (large)',
    'assistant.cap_xlarge': '30 kg and above',
    'assistant.pan_small': 'Small (≤ 100 mm)',
    'assistant.pan_mid': 'Medium (100–180 mm)',
    'assistant.pan_large': 'Large (≥ 180 mm)',
    'assistant.pan_any': 'Any',
    'assistant.cancel': 'Cancel',
    'assistant.back': 'Back',
    'assistant.next': 'Next',
    'assistant.recap': 'Summary:',
    'assistant.range_min': 'Min',
    'assistant.range_max': 'Max',
    'assistant.res_finest': 'Finest',
    'assistant.res_coarsest': 'Coarsest',
    'assistant.loading_specs': 'Loading catalog values…',

    // Match quality badges
    'match.exact': 'Exact match',
    'match.finer': 'More precise',
    'match.larger': 'Larger capacity',
    'match.pan_only': 'Pan match',
    'match.capacity_label': 'Capacity',
    'match.resolution_label': 'Resolution',
    'match.pan_label': 'Pan',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('catalog-language');
    return (saved === 'en' || saved === 'fr') ? saved : 'fr';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('catalog-language', lang);
  }, []);

  const t = useCallback((key: string): string => {
    return translations[language][key] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

const defaultLanguageContext: LanguageContextType = {
  language: 'fr',
  setLanguage: () => {},
  t: (key: string) => translations['fr'][key] || key,
};

export function useLanguage() {
  const context = useContext(LanguageContext);
  return context ?? defaultLanguageContext;
}

// Helper to get localized field from an object
export function getLocalizedField<T extends Record<string, unknown>>(
  obj: T,
  fieldBase: string,
  language: Language,
  fallbackField?: string
): string {
  const localizedKey = `${fieldBase}_${language}` as keyof T;
  const otherLang = language === 'fr' ? 'en' : 'fr';
  const otherLangKey = `${fieldBase}_${otherLang}` as keyof T;
  const fallbackKey = (fallbackField || fieldBase) as keyof T;
  
  const value = obj[localizedKey];
  if (value && typeof value === 'string') return value;
  
  const otherValue = obj[otherLangKey];
  if (otherValue && typeof otherValue === 'string') return otherValue;
  
  const fallback = obj[fallbackKey];
  if (fallback && typeof fallback === 'string') return fallback;
  
  return '';
}
